import is from '@sindresorhus/is';
import { z } from 'zod';

import { jobProfileReviewerCandidateSchema } from './job-profile';

// The acting user behind a notification. Reuses the existing reviewer/UserShort
// contract (id, firstName, lastName, optional emailAddress).
export const notificationActorSchema = jobProfileReviewerCandidateSchema;

export type NotificationActor = z.infer<typeof notificationActorSchema>;

export const notificationTypeSchema = z.enum(['comment_added', 'published', 'reviewer_assigned']);

export type NotificationType = z.infer<typeof notificationTypeSchema>;

export const notificationItemKindSchema = z.enum(['job_cluster', 'job_family', 'job_level', 'job_role']);

export type NotificationItemKind = z.infer<typeof notificationItemKindSchema>;

export const notificationSubjectKindSchema = z.enum(['comment', 'job_cluster', 'job_family', 'job_level', 'job_role']);

export type NotificationSubjectKind = z.infer<typeof notificationSubjectKindSchema>;

export const notificationReadStateSchema = z.enum(['all', 'read', 'unread']);

export type NotificationReadState = z.infer<typeof notificationReadStateSchema>;

const notificationViewBaseSchema = z.object({
    architectureId: z.string(),
    clusterId: z.string().optional(),
    familyId: z.string().optional(),
    subjectTitle: z.string().optional(),
    versionNo: z.number().optional(),
});

const singleNotificationViewBaseSchema = notificationViewBaseSchema.extend({
    actor: notificationActorSchema.optional(),
    createdAt: z.iso.datetime(),
    id: z.string(),
    readAt: z.iso.datetime().optional(),
});

export const itemSingleNotificationViewSchema = singleNotificationViewBaseSchema.extend({
    kind: z.literal('item_single'),
    subjectId: z.string(),
    subjectKind: notificationItemKindSchema,
    type: z.enum(['published', 'reviewer_assigned']),
});

export type ItemSingleNotificationView = z.infer<typeof itemSingleNotificationViewSchema>;

// All comments on a job-architecture entity collapsed into a single thread row.
// `notificationIds` lists the UNREAD comment ids (empty for a fully-read thread); the
// unread count is simply its length.
export const commentThreadNotificationViewSchema = notificationViewBaseSchema.extend({
    itemId: z.string(),
    itemKind: notificationItemKindSchema.optional(),
    kind: z.literal('comment_thread'),
    latestCreatedAt: z.iso.datetime(),
    notificationIds: z.array(z.string()),
    type: z.literal('comment_added'),
});

export type CommentThreadNotificationView = z.infer<typeof commentThreadNotificationViewSchema>;

// A single (non-thread) inbox row: a reviewer_assigned or published item notification.
export type SingleNotificationView = ItemSingleNotificationView;

export const notificationListItemSchema = z.discriminatedUnion('kind', [
    itemSingleNotificationViewSchema,
    commentThreadNotificationViewSchema,
]);

export type NotificationListItem = z.infer<typeof notificationListItemSchema>;

export const isNotificationRead = (notification: Pick<SingleNotificationView, 'readAt'>): boolean =>
    is.string(notification.readAt);

export const isCommentThread = (item: NotificationListItem): item is CommentThreadNotificationView =>
    item.kind === 'comment_thread';

export const isNotificationListItemUnread = (item: NotificationListItem): boolean => {
    if (isCommentThread(item)) {
        return item.notificationIds.length > 0;
    }

    return !isNotificationRead(item);
};

export const notificationPageSchema = z.object({
    items: notificationListItemSchema.array(),
    nextCursor: z.string().optional(),
});

export type NotificationPage = z.infer<typeof notificationPageSchema>;

export const markNotificationsReadBatchRequestSchema = z.object({
    ids: z.array(z.string()).min(1),
});

export type MarkNotificationsReadBatchRequest = z.infer<typeof markNotificationsReadBatchRequestSchema>;

export const unreadNotificationCountSchema = z.object({
    count: z.number().int().nonnegative(),
});

export type UnreadNotificationCount = z.infer<typeof unreadNotificationCountSchema>;
